'use client';

import { css, cx } from '@repo/pandacss/styled-system/css';
import { useId, useState } from 'react';
import { HORIZONTAL_OFFSET, VERTICAL_OFFSET } from './constants';
import type { TooltipProps } from './types';
import { useTooltipPosition } from './useTooltipPosition';

const baseContainerStyles = css({
  position: 'relative',
  display: 'inline-block',
  boxSizing: 'border-box',
});

const hoverAlwaysStyles = css({
  '&:hover [data-tooltip]': {
    opacity: '[1]',
    visibility: '[visible]',
    pointerEvents: '[auto]',
  },
});

const hoverWhenEnabledStyles = css({
  '@media (hover: hover) and (pointer: fine)': {
    "&[data-hover-enabled='true']:hover [data-tooltip]": {
      opacity: '[1]',
      visibility: '[visible]',
      pointerEvents: '[auto]',
    }
  }
});

export function Tooltip({
  tooltipContent,
  children,
  tooltipClassName,
  customTooltipId,
  disableTouchEvents = false,
  'data-testid': dataTestId,
}: TooltipProps) {
  const [hoverPointerType, setHoverPointerType] = useState<string | null>(null);
  const defaultTooltipId = useId();
  const tooltipId = customTooltipId ?? defaultTooltipId;
  const { triggerRef, tooltipRef, position } = useTooltipPosition();

  const containerStyles = cx(
    baseContainerStyles,
    disableTouchEvents ? hoverWhenEnabledStyles : hoverAlwaysStyles,
  );

  // Stays null until a pointer enters, so the reveal is off until we know what the
  // user is pointing with. A browser that reports no pointerType is treated as
  // hover-capable rather than as a tap.
  const isHoverEnabled =
    !disableTouchEvents || (hoverPointerType !== null && hoverPointerType !== 'touch');
  const isVisible = hoverPointerType !== null && isHoverEnabled;

  const tooltipStyles = cx(
    css({
      opacity: '[0]',
      visibility: '[hidden]',
      transitionProperty: '[opacity]',
      transitionDuration: '[0.2s]',
      transitionTimingFunction:
        '[ease-in-out, visibility 0.2s ease-in-out, transform 0.2s ease-in-out]',
      pointerEvents: '[none]',
      position: '[absolute]',
      backgroundColor: 'neutral.white',
      borderTopLeftRadius: 'md',
      borderTopRightRadius: 'md',
      borderBottomRightRadius: 'md',
      borderBottomLeftRadius: 'md',
      boxShadow:
        '[rgba(0, 0, 0, 0.2) 0px 6px 6px -3px, rgba(0, 0, 0, 0.14) 0px 10px 14px 1px, rgba(0, 0, 0, 0.12) 0px 4px 18px 3px]',
      borderTopWidth: 'thin',
      borderRightWidth: 'thin',
      borderBottomWidth: 'thin',
      borderLeftWidth: 'thin',
      borderTopStyle: '[solid]',
      borderRightStyle: '[solid]',
      borderBottomStyle: '[solid]',
      borderLeftStyle: '[solid]',
      borderTopColor: 'neutral.lighter',
      borderRightColor: 'neutral.lighter',
      borderBottomColor: 'neutral.lighter',
      borderLeftColor: 'neutral.lighter',
      paddingTop: '4',
      paddingRight: '4',
      paddingBottom: '4',
      paddingLeft: '4',
      zIndex: '[1000]',
      minWidth: '[200px]',
      maxWidth: '[min(90vw, 90dvw)]',
      // Bottom position
      '&[data-position="bottom"]': {
        right: '[50%]',
        transform: '[translateX(50%)]',
        top: '[calc(100% + var(--vertical-offset))]',
        _before: {
          content: '""',
          position: '[absolute]',
          top: '[-11px]',
          left: '[50%]',
          transform: '[translateX(-50%) rotate(45deg)]',
          width: '4',
          height: '4',
          backgroundColor: 'neutral.white',
          borderTopColor: 'neutral.lighter',
          borderTopStyle: '[solid]',
          borderTopWidth: 'thin',
          borderLeftColor: 'neutral.lighter',
          borderLeftStyle: '[solid]',
          borderLeftWidth: 'thin',
          borderRightStyle: 'none',
          borderBottomStyle: 'none',
          zIndex: '[2]',
        },
        _after: {
          content: '""',
          position: '[absolute]',
          top: '[-9px]',
          left: '[50%]',
          transform: '[translateX(-50%)]',
          width: 'full',
          height: '[9px]',
          zIndex: '[1]',
        },
      },
      // Top position
      '&[data-position="top"]': {
        right: '[50%]',
        transform: '[translateX(50%)]',
        bottom: '[calc(100% + var(--vertical-offset))]',
        _before: {
          content: '""',
          position: '[absolute]',
          bottom: '[-11px]',
          left: '[50%]',
          transform: '[translateX(-50%) rotate(225deg)]',
          width: '4',
          height: '4',
          backgroundColor: 'neutral.white',
          borderTopColor: 'neutral.lighter',
          borderTopStyle: '[solid]',
          borderTopWidth: 'thin',
          borderBottomStyle: 'none',
          borderRightStyle: 'none',
          borderLeftWidth: 'thin',
          borderLeftStyle: '[solid]',
          borderLeftColor: 'neutral.lighter',
          zIndex: '[2]',
        },
        _after: {
          content: '""',
          position: '[absolute]',
          bottom: '[-9px]',
          left: '[50%]',
          transform: '[translateX(-50%)]',
          width: 'full',
          height: '[9px]',
          zIndex: '[1]',
        },
      },
      // Left position
      '&[data-position="left"]': {
        bottom: '[50%]',
        transform: '[translateY(50%)]',
        right: '[calc(100% + var(--horizontal-offset))]',
        _before: {
          content: '""',
          position: '[absolute]',
          right: '[-11px]',
          top: '[50%]',
          transform: '[translateY(-50%) rotate(135deg)]',
          width: '4',
          height: '4',
          backgroundColor: 'neutral.white',
          borderTopColor: 'neutral.lighter',
          borderTopStyle: '[solid]',
          borderTopWidth: 'thin',
          borderRightStyle: 'none',
          borderBottomStyle: 'none',
          borderLeftColor: 'neutral.lighter',
          borderLeftStyle: '[solid]',
          borderLeftWidth: 'thin',
          zIndex: '[2]',
        },
        _after: {
          content: '""',
          position: '[absolute]',
          right: '[-16px]',
          top: '[50%]',
          transform: '[translateY(-50%)]',
          width: '4',
          height: 'full',
          zIndex: '[1]',
        },
      },
      // Right position
      '&[data-position="right"]': {
        bottom: '[50%]',
        transform: '[translateY(50%)]',
        left: '[calc(100% + var(--horizontal-offset))]',
        _before: {
          content: '""',
          position: '[absolute]',
          left: '[-11px]',
          top: '[50%]',
          transform: '[translateY(-50%) rotate(-45deg)]',
          width: '4',
          height: '4',
          backgroundColor: 'neutral.white',
          borderTopColor: 'neutral.lighter',
          borderTopStyle: '[solid]',
          borderTopWidth: 'thin',
          borderRightStyle: 'none',
          borderBottomStyle: 'none',
          borderLeftColor: 'neutral.lighter',
          borderLeftStyle: '[solid]',
          borderLeftWidth: 'thin',
          zIndex: '[2]',
        },
        _after: {
          content: '""',
          position: '[absolute]',
          left: '[-16px]',
          top: '[50%]',
          transform: '[translateY(-50%)]',
          width: '4',
          height: 'full',
          zIndex: '[1]',
        },
      },
    }),
    tooltipClassName,
  );

  return (
    <div
      className={containerStyles}
      data-hover-enabled={isHoverEnabled}
      onPointerEnter={event => setHoverPointerType(event.pointerType)}
      onPointerLeave={() => setHoverPointerType(null)}
    >
      <div ref={triggerRef} aria-describedby={`tooltip-${tooltipId}`}>
        {children}
      </div>
      <div
        ref={tooltipRef}
        aria-hidden={!isVisible}
        role="tooltip"
        id={`tooltip-${tooltipId}`}
        className={tooltipStyles}
        data-tooltip=""
        data-position={position}
        data-testid={dataTestId}
        style={
          {
            '--vertical-offset': `${VERTICAL_OFFSET}px`,
            '--horizontal-offset': `${HORIZONTAL_OFFSET}px`,
          } as React.CSSProperties
        }
      >
        {tooltipContent}
      </div>
    </div>
  );
}
